// 공용 상수 — 도메인 전반에서 반복되는 문자열 리터럴 SSOT + 환경 신호 해석.
// 매직 스트링 제거로 오타·rename 안전성 확보 + IDE 자동완성·find-references 활성화.
//
// ⚠️ 계약 확장 (IMP-432 #380, PR #545 review R4): 본 모듈은 순수 리터럴 모듈이었으나
// "환경 신호 3계열" 블록이 들어오면서 `process.env` 를 읽고 import 시점에 warn 부수효과를 낸다.
// 이는 잠정 배치다 — 리터럴 모듈이 관측 정책을 갖는 것도 층 위반이므로, IMP-435(#383)에서
// env 해석·부수효과를 `config/env.js` 로 분리한다. 그때까지 같은 배치 판단을 반복하지 않도록
// 여기에 명시해 둔다. 신규 리터럴은 종전대로 이 파일에 추가하면 된다.

// ─── 회사 org / 라이선스 역할 값 ──────────────────────────────────────────────
// `companyInfo.org` 및 `getLicenseRole()` 반환값에 동일하게 쓰임.
export const ORG_HQ = 'HQ';
export const ORG_BRANCH = 'Branch';
export const ORG_SUBSIDIARY = 'Subsidiary';

// ─── UserType() 반환 도메인 ───────────────────────────────────────────────────
// UserType() 함수가 반환하는 값의 상수. org 입력 상수(ORG_*)와 구분.
// HQ 반환은 기존 ORG_HQ('HQ')를 재사용 — 신규 USER_TYPE_HQ 불필요.
export const USER_TYPE_BRANCH = 'BRANCH';
export const USER_TYPE_DEALER = 'DEALER';

// ─── 라이선스 역할 — 딜러 분기 ───────────────────────────────────────────────
// `getLicenseRole()` 반환값. dealerType ('internal'|'external') + isTypeChange 조합 결과.
export const ROLE_INTERNAL_DEALER = 'InternalDealer';
export const ROLE_EXTERNAL_DEALER = 'ExternalDealer';

// ─── 실행 환경 값 ────────────────────────────────────────────────────────────
// CheckEnv 결과, NODE_ENV, Sentry environment 등에서 반복 사용.
export const ENV_DEVELOPMENT = 'development';
export const ENV_PRODUCTION = 'production';

// ─── 환경 신호 3계열 (SSoT 서술) ─────────────────────────────────────────────
// IMP-432 (#380) / PR #545 review R3.
// FE 의 "어느 환경인가" 는 단일 축이 아니다. 서로 무관한 3개 신호가 각각 다른 대상을 결정하며,
// 하나로 묶으면 한쪽 정책 변경이 다른 쪽으로 새어 나간다 — 구 `ddb.js getEnv()` 의 결함이 그것이다
// (로컬에서 DDB 를 prod 로 보려고 고치자 Sentry 까지 켜져 프로덕션 프로젝트가 오염, 실측 7건).
//
//   신호                          결정 대상                      배선 출처
//   ──────────────────────────────────────────────────────────────────────────────────────
//   `lib/ddb.js DDB_ENV`          DDB 업스트림 URL               코드 고정 production
//                                                                (dev DDB 인프라 부재)
//   `DEPLOY_CHANNEL` (아래)        Sentry environment·샘플레이트   env `REACT_APP_NODE_ENV`
//   `config/index.js CheckEnv()`   rayguard-api·User·Group API    코드 고정 development
//                                                                (prod 인프라 실재 여부 미확정)
//
// 따라서 같은 빌드가 Sentry 에는 production 으로 보고되면서 API 는 raydevelop 을 호출한다.
// 의도된 상태다. 판정 위치를 한 모듈로 모으는 작업은 IMP-435(#383) — 단 "위치만 모으고 축은
// 분리 유지" 가 그 이슈의 명시 방침이다(값 통일은 getEnv() 회귀).
//
// ⚠️ `REACT_APP_NODE_ENV` 배선 현황 — **GitHub origin 에서 실행되는 경로에는 development
//   생산자가 없다.** (in-repo 생산자는 존재하나 미실행이고, repo 밖 경로는 여기서 확인할 수 없다.
//   "도달 경로 없음" 으로 단정하지 않는다 — review R4/R5)
//   [repo 내 — 확인 가능]
//     `bitbucket-pipelines.yml:29,64`               → 값 있음(development/production). 단 origin 이
//                                                     GitHub 단독이라 **미실행** legacy
//     `.github/workflows/deploy-production.yml`      → 미설정 (미배선 기본값 production 에 의존)
//     `package.json deploy:dev / :prod / :prod-new`  → 미설정
//     `package.json client:dev`(`env-cmd -f .env.dev`) → env 파일 로드 경로이지만 ⓐ `env-cmd` 가
//                                     의존성에 없어 실행 불가이고 ⓑ `react-scripts start` 라
//                                     NODE_ENV=development → `enabled:false`. 즉 배선돼도 **Sentry
//                                     채널 도달 경로는 아니다**
//   [repo 밖 — 확인 불가]
//     dotenv 파일 — `.gitignore` 로 추적 제외. CRA 의 정규 `REACT_APP_*` 주입 채널이다.
//       `yarn deploy:dev` 는 `craco build`(NODE_ENV=production) 이므로 로드 대상은
//       `.env` / `.env.local` / `.env.production[.local]` 이며 **`.env.development` 는 로드되지 않는다**
//     Cloudflare Pages 대시보드 환경 변수 — 플랫폼 측 설정
//   배선은 IMP-433(#381) → IMP-435(#383) 에서 처리한다(`cross-env` 가 이미 devDependency 라
//   스크립트 1줄이면 되지만 빌드 설정 변경은 승인 대상). 실제 채널 분포는 코드가 아니라
//   **Sentry environment 실측**으로 확인하는 것이 정확하다 — #383 완료 기준에 포함.
//   ※ 실측 시 주의: bitbucket develop 의 배포 대상(S3 `rayteams-website-dev/rayguard.rayteams.com`)은
//     `package.json s3sync:dev` 와 **동일 호스트**다. 같은 호스트에서 environment 가 두 값으로
//     섞여 나오는지를 봐야 의미가 있다.
export const DEPLOY_CHANNELS = [ENV_PRODUCTION, ENV_DEVELOPMENT];

// 화이트리스트 매핑 — 미지값(`staging`, 오타)이 production 을 사칭하지 않도록 명시 목록만 인정한다.
// 미배선(빈 값)은 production 으로 폴백한다: 실 운영 배포(deploy-production.yml)가 이 변수를
// export 하지 않으므로, 기본을 development 로 두면 실제 운영 이벤트가 dev 로 오분류된다.
// 다만 "미배선" 과 "미지값" 은 구분해 후자는 warn 으로 관측한다(무음 금지, CLAUDE.md §7).
export const resolveDeployChannel = (raw) => {
    if (raw === undefined || raw === null || raw === '') return ENV_PRODUCTION;
    if (DEPLOY_CHANNELS.indexOf(raw) !== -1) return raw;
    console.warn('[guard] resolveDeployChannel: 미지 채널값 — production 으로 폴백', {
        handler: 'resolveDeployChannel',
        raw,
        allowed: DEPLOY_CHANNELS,
        reason: 'REACT_APP_NODE_ENV 값이 화이트리스트 밖',
    });
    return ENV_PRODUCTION;
};

export const DEPLOY_CHANNEL = resolveDeployChannel(process.env.REACT_APP_NODE_ENV);

// ─── 로컬 개발 hostname ─────────────────────────────────────────────────────
// 환경 판별, dev-only 기능 활성화 분기에 사용.
export const HOST_LOCALHOST = 'localhost';
export const HOST_LOOPBACK_IPV4 = '127.0.0.1';

// ─── 환경별 도메인 ──────────────────────────────────────────────────────────
// IMP-432 (#380) 이후 두 상수 모두 런타임 소비처가 없다 — 호스트 기반 환경 판별
// (`ddb.js getEnv()`) 을 제거하고 위 "환경 신호 3계열" 로 대체했다.
// 호스트 분기를 되살릴 때 쓰는 예약 상수.
export const DOMAIN_RAYMEDICAL = 'raymedical.com';
export const DOMAIN_RAYDEVELOP = 'raydevelop.com';

// ─── 라이선스 필터 — Status / Type 값 SSOT ────────────────────────────────────
// IMP-137 PR #215 review (Arch Major) — Customer Licenses 탭(Subsidiary/Dealer) 양쪽
// 케이싱 통일. 필터 UI / FE 분기 / BE 페치 파라미터에 모두 동일 값 사용.
//
// Status: BE 의 voucher_license.status 와 정렬 — 'active' / 'suspend' (NOT 'suspended').
//   'expired' 는 BE 미지원 FE-only 분기로 voucher.expired <= Date.now() 직접 검사.
//   UI 라벨은 별도 i18n 키 ('Active'/'Suspended'/'Expired') 로 표시.
export const LICENSE_STATUS_ALL = 'all';
export const LICENSE_STATUS_ACTIVE = 'active';
export const LICENSE_STATUS_SUSPEND = 'suspend';
export const LICENSE_STATUS_EXPIRED = 'expired';

// Type: voucher_license.type 매핑. isRenewalLicense 헬퍼와 정합.
export const LICENSE_TYPE_ALL = 'all';
export const LICENSE_TYPE_INITIAL = 'initial';
export const LICENSE_TYPE_RENEWAL = 'renewal';

// ─── DDB(SmartDent License) upstream URL ────────────────────────────────────
// IMP-432 (#380) — 현재 런타임은 호스트와 무관하게 항상 ③ URL_DDB_PROD 를 쓴다
// (`ddb.js` DDB_ENV = production 고정. dev DDB 인프라 부재 → 로컬 개발도 prod DB 직접).
// ①②는 dev DDB 인프라 복구 시 되살릴 경로로 보존 — 현재 프로덕션 소비처 0(테스트만 참조).
//
// 라우팅 정책(`resolveDdbApiUrl` 룩업 계약):
//   ① setupProxy 가용 환경(localhost / 127.0.0.1 / IP 직접 접속) → URL_DDB_DEV_PROXY
//      (same-origin 상대 경로 → setupProxy.js 가 server-side RSA + upstream forward)
//   ② 그 외 dev 환경(*.raydevelop.com / production FE 호스트 등) → URL_DDB_DEV
//   ③ prod 환경 → URL_DDB_PROD
export const URL_DDB_DEV_PROXY = '/proxy/calldynamo-dev';
export const URL_DDB_DEV = 'https://api-teams-info.raydevelop.com/common/calldynamo';
export const URL_DDB_PROD = 'https://api-teams-info.rayteams.com/common/calldynamo';
